import React, { useEffect, useState } from 'react';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { OnboardingContainer } from '../../../components/onboarding/OnboardingContainer';
import {
  Step1BusinessInfo,
  Step2ContactInfo,
  Step3DocumentUpload,
  Step4ACHSetup,
  Step5ReviewSignature,
} from '../../../components/onboarding/steps';
import { onboardingApi } from '../../../services/onboardingApi';
import { adminApi } from '../../../services/adminApi';

interface AdminOnboardCustomerProps {
  /** Called after the customer is created (application submitted + approved). */
  onDone: (email: string) => void;
  /** Called when the admin cancels the wizard. */
  onCancel: () => void;
}

/**
 * Admin-initiated customer onboarding. Reuses the exact same multi-step
 * onboarding form customers fill out, but on submit it (1) creates the
 * application via the public submit endpoint, then (2) immediately approves it
 * as the admin — which provisions the customer's user account + profile.
 */
export const AdminOnboardCustomer: React.FC<AdminOnboardCustomerProps> = ({ onDone, onCancel }) => {
  const {
    currentStep,
    formData,
    uploadedFiles,
    setSubmitting,
    clearErrors,
    resetForm,
  } = useOnboardingStore();

  const [submitError, setSubmitError] = useState<string | null>(null);

  // Start from a clean form each time (the store persists to localStorage and is
  // shared with the public flow).
  useEffect(() => {
    resetForm();
  }, [resetForm]);

  const handleSubmit = async () => {
    setSubmitting(true);
    clearErrors();
    setSubmitError(null);
    try {
      // 1. Create the application (same endpoint customers use).
      const res = await onboardingApi.submitApplication(formData, uploadedFiles);
      // 2. Approve it right away → creates the customer's account + profile.
      const approval = await adminApi.approveApplication(Number(res.applicationId));
      const email = approval.user?.email ?? formData.contactEmail;
      resetForm();
      onDone(email);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to onboard customer');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: return <Step1BusinessInfo />;
      case 2: return <Step2ContactInfo />;
      case 3: return <Step3DocumentUpload />;
      case 4: return <Step4ACHSetup />;
      case 5: return <Step5ReviewSignature />;
      default: return null;
    }
  };

  return (
    <div>
      {submitError && (
        <div style={{ margin: '12px auto', maxWidth: 720, padding: 12, background: '#FEF2F2', color: '#B91C1C', borderRadius: 8, fontSize: 14 }}>
          {submitError}
        </div>
      )}
      <OnboardingContainer onSubmit={handleSubmit} onBack={onCancel} backLabel="Cancel">
        {renderStepContent()}
      </OnboardingContainer>
    </div>
  );
};

export default AdminOnboardCustomer;
